import { useMemo } from "react";

import { isLegacySKU } from "../../../common/variables/skuOptions";
import {
  AggregateStatus,
  IDeviceComponent,
  ISKUGroup,
} from "../types/groupedDevices";
import { IDeviceTableRow, useDeviceTableRows } from "./useDeviceTableRows";

interface IUseGroupedDeviceTableRows {
  groups: ISKUGroup[];
  loading: boolean;
}

/**
 * Transforms flat device rows into grouped structure.
 * - Legacy SKU devices are grouped together
 * - Devices with SKU AND SN are grouped by SKU-SN combo
 * - All other devices (missing SKU or SN) are grouped as "ungrouped"
 * @return {IUseGroupedDeviceTableRows} Groups of devices
 */
export const useGroupedDeviceTableRows = (): IUseGroupedDeviceTableRows => {
  const { rows, loading } = useDeviceTableRows();

  const groups = useMemo((): ISKUGroup[] => {
    if (loading || !rows.length) {
      return [];
    }

    // Map to group devices: legacy, SKU+SN combo, or ungrouped
    const groupMap = new Map<string, IDeviceTableRow[]>();

    rows.forEach(row => {
      // Determine group: legacy first, then SKU+SN, else ungrouped
      let groupKey: string;

      if (isLegacySKU(row.skuNumber)) {
        groupKey = "legacy";
      } else if (row.skuNumber && row.serialNumber) {
        groupKey = `${row.skuNumber}-${row.serialNumber}`;
      } else {
        groupKey = "ungrouped";
      }

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }

      groupMap.get(groupKey)!.push(row);
    });

    // Transform map to ISKUGroup array
    const result: ISKUGroup[] = [];

    groupMap.forEach((deviceRows, groupKey) => {
      // Use first device for group-level data (all should have same SKU info)
      const firstDevice = deviceRows[0];

      // Transform device rows to IDeviceComponent
      const devices: IDeviceComponent[] = deviceRows.map(row => ({
        rowId: row.rowId,
        deviceName: row.name,
        node: {
          id: row.node.id,
          name: row.node.name ?? row.node.id.split("#N#").at(-1) ?? "",
        },
        cameraIpAddress: row.cameraIpAddress,
        isOnline: row.isOnline,
        models: row.models,
        modelsToRun: row.modelsToRun,
        componentSerialNumber: row.componentSerialNumber,
        originalRow: row,
      }));

      devices.sort((a, b) => a.deviceName.localeCompare(b.deviceName));

      // Aggregate status: online (all), degraded (some), offline (none)
      const onlineCount = devices.filter(d => d.isOnline).length;
      const aggregateStatus: AggregateStatus =
        onlineCount === devices.length
          ? "online"
          : onlineCount === 0
          ? "offline"
          : "degraded";

      const isUngrouped = groupKey === "ungrouped";

      const group: ISKUGroup = {
        groupId: groupKey,
        skuNumber: isUngrouped ? null : firstDevice.skuNumber ?? null,
        serialNumber: isUngrouped ? null : firstDevice.serialNumber ?? null,
        aggregateStatus,
        devices,
        isLegacy: isLegacySKU(firstDevice.skuNumber),
        parentProductSerialNumber: isUngrouped
          ? null
          : firstDevice.parentProductSerialNumber ?? null,
      };

      result.push(group);
    });

    // Sort groups: SKU+SN groups first, then legacy, then ungrouped
    result.sort((a, b) => {
      const aIsSkuSn = a.groupId !== "legacy" && a.groupId !== "ungrouped";
      const bIsSkuSn = b.groupId !== "legacy" && b.groupId !== "ungrouped";

      // SKU+SN groups come first
      if (aIsSkuSn && !bIsSkuSn) return -1;

      if (!aIsSkuSn && bIsSkuSn) return 1;

      // Legacy before ungrouped
      if (a.groupId === "legacy" && b.groupId === "ungrouped") return -1;

      if (a.groupId === "ungrouped" && b.groupId === "legacy") return 1;

      // Within SKU+SN groups, sort by SKU then SN
      if (a.skuNumber && b.skuNumber) {
        const skuCompare = a.skuNumber.localeCompare(b.skuNumber);

        if (skuCompare !== 0) return skuCompare;
      }

      if (a.serialNumber && b.serialNumber) {
        return a.serialNumber.localeCompare(b.serialNumber);
      }

      return 0;
    });

    return result;
  }, [rows, loading]);

  return {
    groups,
    loading,
  };
};
